import type { EnvProvider } from '@evc/web-components';
import { ENV_VARIABLES } from '@evc/web-components';

import type { Env, PlatformEnv } from './env.type';
import { ENVIRONMENT } from './env.type';

function getUris(domain: string, domainProd: string): Record<Env, string> {
  const localUri = `https://${domain}.api.ca.connect.evcnt.local/`;
  const developmentUri = `https://${domain}.api.ca.connect.dev-evcnt.com/`;
  return {
    connect_local: localUri,
    connect_testing: localUri,
    local: developmentUri,
    testing: developmentUri,
    development: developmentUri,
    staging: `https://${domain}.api.ca.connect.stg-evcnt.com/`,
    production: `https://${domain}.api.ca.connect.${domainProd}/`,
  };
}

/** ensure some default values for our environment config */
function environmentFactory(partialEnv: Partial<PlatformEnv>): PlatformEnv {
  const environment: PlatformEnv = {
    ENV: ENVIRONMENT.production,
    LOCALES: ['en', 'fr'],
    LOCALES_STORAGE_KEY: 'evcnt_user_lang',
    ...partialEnv,
  } as PlatformEnv;

  // ensure USE_CONNECT_PLATFORM is false if no min requirements
  environment.USE_CONNECT_PLATFORM = (() => {
    const { AUTH_CLIENT_ID, USE_CONNECT_PLATFORM: USE_CONNECT_PLATFORM } = environment;
    const isTrully = USE_CONNECT_PLATFORM === true || (USE_CONNECT_PLATFORM as unknown) === 'true';
    const haveAuthClientId = (AUTH_CLIENT_ID && AUTH_CLIENT_ID !== '') || false;

    return (isTrully && haveAuthClientId) || false;
  })();

  if (environment.USE_CONNECT_PLATFORM) {
    const { hostname } = window.location;
    const forceLegacy = hostname === 'localhost' && environment.AUTH_USE_LEGACY_DOMAIN;
    const domainProd =
      forceLegacy || /tnmconnect\.com$/.test(hostname) ? 'tnmconnect.com' : 'evidentscientific.com';

    const coreUris = getUris('core', domainProd);
    const storageUris = getUris('storage', domainProd);
    const deviceUris = getUris('device', domainProd);

    environment.PROTECTED_URIS = {
      ...environment.PROTECTED_URIS,
      ...(!environment.PROTECTED_URIS?.CORE && {
        CORE: coreUris[environment.ENV!] ?? coreUris.production,
      }),
      ...(!environment.PROTECTED_URIS?.STORAGE && {
        STORAGE: storageUris[environment.ENV!] ?? storageUris.production,
      }),
      ...(!environment.PROTECTED_URIS?.DEVICE && {
        DEVICE: deviceUris[environment.ENV!] ?? deviceUris.production,
      }),
    };
  }

  return environment as PlatformEnv;
}

/** please use this one if using the platform lib
 * - use the one from web-components only if using the web-components lib standalone
 */
export function provideEnv(environment: PlatformEnv): EnvProvider {
  return {
    provide: ENV_VARIABLES,
    useFactory: () => environmentFactory(environment),
  };
}
